import axios from './axiosSetup';

class CookieConsentConfig {
  constructor() {
    this.listeners = [];
    this.config = this.loadConfig();
  }

  loadConfig() {
    try {
      const stored = localStorage.getItem('cookie_consent_config');
      if (stored) return JSON.parse(stored);
    } catch (e) {
      console.warn('Failed to load cookie config', e);
    }

    return {
      tenantId: null,
      apiBase: '',
      appName: '',
      content: '<h3>🍪 Cookie Consent</h3><p>We use cookies to enhance your experience. By continuing to visit this site you agree to our use of cookies.</p>',
      aboutTitle: 'About this tool',
      aboutContent: 'This cookie consent tool is powered by Whitehats Technologies. It helps websites comply with privacy regulations like GDPR and CCPA by collecting informed consent from users before setting cookies.',
      aboutLinkText: 'Whitehats Technologies',
      aboutLinkUrl: 'https://www.whitehats.tech/',
      buttonStyle: 'rounded',
      placement: 'bottom',
      backgroundColor: '#ffffff',
      shade: 'light',
      containerShape: 'rounded',
      size: 'medium',
      preferences: {
        necessary: true,
        performance: true,
        functional: false,
        targeting: true,
        social: false
      },
      categories: [
        { id: 'necessary', title: 'Strictly Necessary Cookies', description: 'These cookies are essential for the website to function properly and cannot be disabled.', required: true, alwaysOn: true },
        { id: 'performance', title: 'Performance Cookies', description: 'These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site.', defaultOn: true, alwaysOn: false },
        { id: 'functional', title: 'Functional Cookies', description: 'These cookies enable the website to provide enhanced functionality and personalization.', defaultOn: false, alwaysOn: false },
        { id: 'targeting', title: 'Targeting Cookies', description: 'These cookies are set through our site by our advertising partners and help deliver relevant adverts.', defaultOn: true, alwaysOn: false },
        { id: 'social', title: 'Social Media Cookies', description: 'These cookies are set by social platforms to enable sharing and social features.', defaultOn: false, alwaysOn: false }
      ]
    };
  }

  saveConfig(updates) {
    this.config = { ...this.config, ...updates };
    try {
      localStorage.setItem('cookie_consent_config', JSON.stringify(this.config));
    } catch (e) {
      console.error('Failed to save config', e);
    }
    this.notifyListeners();
  }

  getConfig() {
    return { ...this.config };
  }

  updateField(field, value) {
    this.saveConfig({ [field]: value });
  }

  updatePreferences(prefs) {
    this.saveConfig({ preferences: { ...this.config.preferences, ...prefs } });
  }

  subscribe(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  }

  notifyListeners() {
    this.listeners.forEach(cb => {
      try { cb(this.config); } catch (e) { console.error('Listener error', e); }
    });
  }

  // ─── Dynamic CDN Embed Code Generator with Banner ID ───────────────────
  getEmbedCode(bannerId, configOverride) {
    const activeCfg = configOverride || this.config;
    const {
      tenantId,
      apiBase,
      appName,
      buttonStyle,
      placement,
      backgroundColor,
      shade,
      containerShape,
      preferences
    } = activeCfg;

    const prefs = preferences || {};
    const selected = [];
    if (prefs.performance) selected.push('performance');
    if (prefs.functional) selected.push('functional');
    if (prefs.targeting) selected.push('targeting');
    if (prefs.social) selected.push('social');

    let consentAttr = 'none';
    if (selected.length === 4) consentAttr = 'all';
    else if (selected.length > 0) consentAttr = JSON.stringify(selected);

    const rawTid = tenantId || this.getTenantIdFromStorage() || 'YOUR_TENANT_ID';
    const tid = rawTid === 'YOUR_TENANT_ID' ? 'YOUR_TENANT_ID' : this.encryptTenantId(rawTid);

    const apiAttr = apiBase ? ` data-api-base="${apiBase}"` : '';
    const appAttr = appName ? ` data-app-name="${appName}"` : '';
    const sizeAttr = activeCfg.size ? ` data-size="${activeCfg.size}"` : '';
    const bannerAttr = bannerId ? ` data-banner-id="${bannerId}"` : '';

    const cookiesAttr = (consentAttr && consentAttr.startsWith('['))
      ? `'${consentAttr.replace(/'/g, "\\'")}'`
      : consentAttr;

    return (
      `<script src="https://consent.whitehats.tech/widget/cookie-consent.js"` +
      ` data-tenant="${tid}"` +
      `${bannerAttr}` +
      `${apiAttr}` +
      `${appAttr}` +
      `${sizeAttr}` +
      ` data-cookies-consent=${cookiesAttr}` +
      ` data-btn-style="${buttonStyle || 'rounded'}"` +
      ` data-placement="${placement || 'bottom'}"` +
      ` data-bg-color="${backgroundColor || '#ffffff'}"` +
      ` data-shade="${shade || 'light'}"` +
      ` data-container-shape="${containerShape || 'rounded'}"` +
      `></` + `script>`
    );
  }

  // ─── Reversible symmetric obfuscation / encryption for Tenant ID ───────
  encryptTenantId(tenantId) {
    if (!tenantId && tenantId !== 0) return 'YOUR_TENANT_ID';
    let rawStr = String(tenantId).trim();
    if (!rawStr || rawStr === 'YOUR_TENANT_ID') return 'YOUR_TENANT_ID';
    if (rawStr.startsWith('dpdp_')) {
      const dec = this.decryptTenantId(rawStr);
      if (dec) rawStr = String(dec);
    }
    if (this._cachedRawTenantId === rawStr && this._cachedEncTenantId) {
      return this._cachedEncTenantId;
    }
    const salt = Math.floor(Math.random() * 0xffff).toString(16).padStart(4, '0');
    const secret = 'OneDPDP@2025#PrivacySecretKey';
    const key = secret + salt;
    const payload = `dpdp:${rawStr}:${salt}`;
    let outHex = '';
    for (let i = 0; i < payload.length; i++) {
      const kChar = key.charCodeAt(i % key.length);
      const sChar = salt.charCodeAt(i % salt.length);
      const enc = (payload.charCodeAt(i) ^ kChar ^ sChar) & 0xff;
      outHex += ('0' + enc.toString(16)).slice(-2);
    }
    const encrypted = 'dpdp_' + salt + outHex;
    this._cachedRawTenantId = rawStr;
    this._cachedEncTenantId = encrypted;
    return encrypted;
  }

  decryptTenantId(token) {
    if (!token && token !== 0) return null;
    const str = String(token).trim();
    if (/^\d+$/.test(str)) return parseInt(str, 10);
    if (!str.startsWith('dpdp_') || str.length < 9) {
      const fallback = parseInt(str, 10);
      return isNaN(fallback) ? null : fallback;
    }
    try {
      const body = str.slice(5);
      const salt = body.slice(0, 4);
      const hex = body.slice(4);
      if (hex.length % 2 !== 0) return null;
      const key = 'OneDPDP@2025#PrivacySecretKey' + salt;
      let decrypted = '';
      for (let i = 0; i < hex.length; i += 2) {
        const byteVal = parseInt(hex.substr(i, 2), 16);
        const idx = i / 2;
        const kChar = key.charCodeAt(idx % key.length);
        const sChar = salt.charCodeAt(idx % salt.length);
        decrypted += String.fromCharCode(byteVal ^ kChar ^ sChar);
      }
      const parts = decrypted.split(':');
      if (parts.length === 3 && parts[0] === 'dpdp' && parts[2] === salt) {
        const tid = parseInt(parts[1], 10);
        return isNaN(tid) ? null : tid;
      }
      return null;
    } catch (e) {
      return null;
    }
  }

  getTenantIdFromStorage() {
    try { return localStorage.getItem('tenant_id'); } catch (e) { return null; }
  }

  getSessionId() {
    try {
      let sid = localStorage.getItem('cookie_session_id');
      if (!sid) {
        sid = 's_' + Math.random().toString(36).slice(2, 11);
        localStorage.setItem('cookie_session_id', sid);
      }
      return sid;
    } catch (e) {
      return 's_' + Math.random().toString(36).slice(2, 11);
    }
  }

  getWidgetStyles(configOverride) {
    const activeCfg = configOverride || this.config;
    const { buttonStyle, placement, backgroundColor, shade, containerShape } = activeCfg;
    const getBorderRadius = () => containerShape === 'rounded' ? '12px' : containerShape === 'square' ? '4px' : '8px';
    const getBoxShadow = () => shade === 'light' ? '0 2px 8px rgba(44,62,80,0.06)' : shade === 'medium' ? '0 4px 16px rgba(44,62,80,0.13)' : '0 6px 24px rgba(44,62,80,0.22)';
    const getButtonBorderRadius = () => buttonStyle === 'pill' ? '9999px' : buttonStyle === 'square' ? '4px' : '8px';
    return {
      container: { backgroundColor: backgroundColor || '#ffffff', borderRadius: getBorderRadius(), boxShadow: getBoxShadow() },
      button: { borderRadius: getButtonBorderRadius() },
      placement: placement || 'bottom'
    };
  }

  // ─── Backend API Cookie Banner Management Helpers ──────────────────────
  _getCandidateUrls(path, apiBase) {
    const cleanPath = path.startsWith('/') ? path : '/' + path;
    const base = (apiBase || this.config.apiBase || (typeof window !== 'undefined' && window.__API_BASE__) || '').toString().trim().replace(/\/+$/, '');
    const set = new Set();
    if (base) {
      if (base.endsWith('/api')) {
        set.add(base + cleanPath);
        set.add(base.replace(/\/api$/, '') + cleanPath);
      } else {
        set.add(base + cleanPath);
        set.add(base + '/api' + cleanPath);
      }
    }
    set.add(cleanPath);
    set.add('/api' + cleanPath);
    return Array.from(set);
  }

  async _request(method, path, data = null, params = null, apiBase = '') {
    const urls = this._getCandidateUrls(path, apiBase);
    let lastError = null;
    const token = (typeof localStorage !== 'undefined' && localStorage.getItem('token')) || '';
    const headers = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    for (const url of urls) {
      try {
        const config = {
          method: method.toLowerCase(),
          url,
          headers,
          data: data ? data : undefined,
          params: params ? params : undefined,
          skipLoading: true
        };
        const response = await axios(config);
        if (response && response.data) {
          return response.data;
        }
      } catch (err) {
        lastError = err;
        const status = err.response ? err.response.status : null;
        if (status === 404 || status === 405) {
          console.warn(`Endpoint ${url} returned ${status}, trying next candidate...`);
          continue;
        }
        const msg = err.response?.data?.detail || err.response?.data?.message || err.message || `HTTP ${status}`;
        throw new Error(msg);
      }
    }

    // Secondary fallback using native fetch
    for (const url of urls) {
      try {
        let fullUrl = url;
        if (params) {
          const qs = new URLSearchParams(params).toString();
          if (qs) fullUrl += (fullUrl.includes('?') ? '&' : '?') + qs;
        }
        const fetchOpts = {
          method: method.toUpperCase(),
          headers
        };
        if (data && (method.toUpperCase() === 'POST' || method.toUpperCase() === 'PUT')) {
          fetchOpts.body = JSON.stringify(data);
        }
        const fRes = await fetch(fullUrl, fetchOpts);
        if (fRes.status === 404 || fRes.status === 405) {
          continue;
        }
        if (!fRes.ok) {
          let errDetail = 'HTTP ' + fRes.status;
          try {
            const errJson = await fRes.json();
            if (errJson && (errJson.detail || errJson.message)) errDetail = errJson.detail || errJson.message;
          } catch (_) { }
          throw new Error(errDetail);
        }
        return await fRes.json();
      } catch (fErr) {
        if (fErr.message && !fErr.message.includes('404') && !fErr.message.includes('405')) {
          lastError = fErr;
        }
      }
    }

    const finalMsg = lastError?.response?.data?.detail || lastError?.response?.data?.message || lastError?.message || 'Failed to connect to cookie banner server';
    throw new Error(finalMsg);
  }

  async fetchBanners(tenantId, apiBase) {
    const tid = tenantId || this.config.tenantId || this.getTenantIdFromStorage();
    if (!tid) return [];
    try {
      const data = await this._request('GET', '/manager/cookie-banners', null, { tenant_id: tid }, apiBase);
      if (data && data.limit !== undefined) {
        this.bannerLimit = data.limit;
      }
      return (data && Array.isArray(data.data)) ? data.data : [];
    } catch (e) {
      console.warn('Failed to fetch cookie banners from API:', e);
      return [];
    }
  }

  async fetchBannerData(tenantId, apiBase) {
    const tid = tenantId || this.config.tenantId || this.getTenantIdFromStorage();
    if (!tid) return { data: [], limit: null };
    try {
      const data = await this._request('GET', '/manager/cookie-banners', null, { tenant_id: tid }, apiBase);
      const limit = data && data.limit !== undefined ? data.limit : null;
      if (limit !== null) this.bannerLimit = limit;
      return {
        data: (data && Array.isArray(data.data)) ? data.data : [],
        limit
      };
    } catch (e) {
      console.warn('Failed to fetch cookie banners from API:', e);
      return { data: [], limit: null };
    }
  }

  async saveBannerToDb(bannerId, payload, tenantId, apiBase) {
    const tid = tenantId || this.config.tenantId || this.getTenantIdFromStorage();
    return await this._request('PUT', `/manager/cookie-banners/${bannerId}`, payload, { tenant_id: tid }, apiBase);
  }

  async createBannerInDb(payload, apiBase) {
    return await this._request('POST', '/manager/cookie-banners', payload, null, apiBase);
  }

  async deleteBannerFromDb(bannerId, tenantId, apiBase) {
    const tid = tenantId || this.config.tenantId || this.getTenantIdFromStorage();
    return await this._request('DELETE', `/manager/cookie-banners/${bannerId}`, null, { tenant_id: tid }, apiBase);
  }

  async setDefaultBannerInDb(bannerId, tenantId, apiBase) {
    const tid = tenantId || this.config.tenantId || this.getTenantIdFromStorage();
    return await this._request('POST', `/manager/cookie-banners/${bannerId}/set-default`, null, { tenant_id: tid }, apiBase);
  }
}

const cookieConsentConfig = new CookieConsentConfig();
if (typeof module !== 'undefined' && module.exports) module.exports = cookieConsentConfig;
if (typeof window !== 'undefined') window.cookieConsentConfig = cookieConsentConfig;
export default cookieConsentConfig;